/* eslint-disable */
import * as React from 'react';
import { Box, FormControl, RadioGroup, FormHelperText } from '@mui/material';
import { FormikProps } from 'formik';
import dayjs from 'dayjs';
import {
  Select,
  Radio,
  TextField,
  Checkbox,
  PhoneInputField,
  DatePicker,
} from '../../../../../components';
import { PAGE_TEXT } from '../../../lang';
import {
  DSRC_DECISION_TYPE,
  EntranceRequirement,
  OFFER_TYPE,
} from '../constants';
import { RadioWithTextField } from './RadioWithTextField';
import { checkInvalidDate } from '../../../../Application/graphql';
import { AdmissionForm } from './types';
import {
  approveOptions,
  entranceRequirementOptions,
  returnOptions,
  conditionTypes,
} from '../options';

const STEP_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT;
const ADMISSION_FORM_TEXT = STEP_TEXT.STEP_G;
const CONTACT_PERSON =
  PAGE_TEXT.DRC_ASSESSMENT_DETAILS.CONDITION_CHECK.CONTACT_PERSON;

const commonSvgIconStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  '& .MuiSvgIcon-root': {
    marginTop: '-0.2rem',
    fontSize: 18,
  },
};

export type AdmissionDecisionProps = {
  isViewMode: boolean;
  requestRequirementWaive: boolean;
  programmeRequirements: Partial<Record<EntranceRequirement, string>>;
  maxMeetConditionDeadline: Date;
  formik: FormikProps<AdmissionForm>;
};

export const AdmissionDecision: React.FunctionComponent<
  AdmissionDecisionProps
> = (props: AdmissionDecisionProps) => {
  const {
    isViewMode,
    requestRequirementWaive,
    programmeRequirements,
    maxMeetConditionDeadline,
    formik,
  } = props;
  const { values, touched, errors, handleChange, handleBlur, setFieldValue } =
    formik;
  const isReturned = values.admissionDecision === DSRC_DECISION_TYPE.RETURN;
  const isApproved =
    values.admissionDecision === DSRC_DECISION_TYPE.OFFER_APPROVED;

  const isConditionOffer = values.offerType === OFFER_TYPE.CONDITION;
  return (
    <Box>
      <div className="my-4">
        <h3 className="font-bold mb-2 text-[#951D1D]">
          {ADMISSION_FORM_TEXT.ADMISSION.TITLE}
        </h3>
      </div>
      <div className="ml-6">
        <Box className="flex flex-start mb-4">
          <span className="mt-[1.25rem]">
            {ADMISSION_FORM_TEXT.ADMISSION.A1}
          </span>
          <Select
            id="admissionDecision"
            name="admissionDecision"
            disabled={isViewMode}
            options={approveOptions}
            value={values.admissionDecision ?? ''}
            InputLabelProps={{ shrink: true }}
            onChange={handleChange}
            onBlur={handleBlur}
            error={
              touched.admissionDecision && Boolean(errors.admissionDecision)
            }
            helperText={touched.admissionDecision && errors.admissionDecision}
            sx={{
              width: '8rem',
              margin: '0.25rem 0.5rem',
            }}
          />
          <span className="mt-[1.25rem]">
            {ADMISSION_FORM_TEXT.ADMISSION.A2}
          </span>
        </Box>
        {isReturned && (
          <Box>
            <span className="mb-4 flex flex-start">
              <span className="mt-[1.25rem]">
                {ADMISSION_FORM_TEXT.ADMISSION.RETURN}
              </span>
              <Select
                name="returnDestination"
                id="returnDestination"
                disabled={isViewMode}
                options={returnOptions}
                required
                value={values.returnDestination ?? ''}
                InputLabelProps={{ shrink: true }}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.returnDestination && Boolean(errors.returnDestination)
                }
                helperText={
                  touched.returnDestination && errors.returnDestination
                }
                sx={{
                  width: '10rem',
                  margin: '0.25rem 0.5rem',
                }}
              />
            </span>
            <TextField
              className="mb-4"
              id="remarks"
              name="remarks"
              label={'Remarks'}
              disabled={isViewMode}
              required
              value={values.remarks ?? undefined}
              InputLabelProps={{ shrink: true }}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.remarks && Boolean(errors.remarks)}
              helperText={touched.remarks && errors.remarks}
              rows={6}
              multiline
              sx={{ width: '90%' }}
            />
          </Box>
        )}
        {isApproved && (
          <>
            <Box className="mb-4">
              <h3
                className={`mb-4 ${
                  requestRequirementWaive
                    ? 'font-bold'
                    : 'text-[#C5C5C5] font-semibold'
                }`}
              >
                {ADMISSION_FORM_TEXT.ADMISSION.B.TITLE}
              </h3>
              <FormControl
                className="!ml-4"
                error={
                  touched.entranceRequirementWaive &&
                  Boolean(errors.entranceRequirementWaive)
                }
              >
                <RadioGroup
                  name="entranceRequirementWaive"
                  value={values.entranceRequirementWaive}
                  onChange={handleChange}
                >
                  {[
                    {
                      value: true,
                      label: ADMISSION_FORM_TEXT.ADMISSION.B.OPTION_1,
                    },
                    {
                      value: false,
                      label: ADMISSION_FORM_TEXT.ADMISSION.B.OPTION_2,
                    },
                  ].map((waive, index) => (
                    <Radio
                      key={index}
                      value={waive.value}
                      size="small"
                      disabled={isViewMode || !requestRequirementWaive}
                      label={
                        <div
                          className="text-sm mt-[0.4em]"
                          dangerouslySetInnerHTML={{
                            __html: waive.label,
                          }}
                        />
                      }
                      sx={{ ...commonSvgIconStyle, marginTop: '0.4em' }}
                    />
                  ))}
                </RadioGroup>
                <FormHelperText>
                  {touched.entranceRequirementWaive &&
                    errors.entranceRequirementWaive}
                </FormHelperText>
              </FormControl>
            </Box>

            <Box>
              <h3 className="mb-4">{ADMISSION_FORM_TEXT.ADMISSION.C}</h3>
              <div className="pl-4">
                <Select
                  className="w-full mb-4 !pr-12"
                  id="offerType"
                  name="offerType"
                  disabled={isViewMode}
                  options={entranceRequirementOptions}
                  value={values.offerType}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.offerType && Boolean(errors.offerType)}
                  helperText={touched.offerType && errors.offerType}
                />
                {isConditionOffer && (
                  <>
                    <div className="py-4">
                      {`${ADMISSION_FORM_TEXT.ADMISSION.SPECIFY_CONDITIONS}:`}
                    </div>
                    <div className="flex flex-col pl-8" id="conditions">
                      {conditionTypes.map((condition, index) => {
                        const { label, value } = condition;
                        const checked = values.conditions[value].ticked;
                        return (
                          <div key={`${index}-${value}`}>
                            <Checkbox
                              className="mb-2"
                              disabled={isViewMode}
                              label={<div className="text-sm">{label}</div>}
                              value={values.conditions[value].ticked}
                              checked={checked}
                              name={`conditions.${value}.ticked`}
                              size="small"
                              onChange={handleChange}
                              error={
                                touched.conditions?.[value]?.ticked &&
                                Boolean(errors.conditions?.[value]?.ticked)
                              }
                            />
                            {checked && (
                              <RadioGroup
                                className="ml-8"
                                name={`conditions.${value}.detailType`}
                                onChange={handleChange}
                                value={values.conditions[value].detailType}
                              >
                                <Radio
                                  value={'default'}
                                  className="text-sm"
                                  disabled={isViewMode}
                                  size="small"
                                  label={
                                    <div
                                      className="text-sm mt-[0.4em]"
                                      dangerouslySetInnerHTML={{
                                        __html: programmeRequirements[
                                          value
                                        ] as string,
                                      }}
                                    />
                                  }
                                  sx={{
                                    ...commonSvgIconStyle,
                                    marginTop: '0.4em',
                                  }}
                                />
                                <RadioWithTextField
                                  value={'custom'}
                                  className="text-sm"
                                  disabled={isViewMode}
                                  size="small"
                                  label={
                                    <div
                                      className="text-sm mt-[0.4em]"
                                      dangerouslySetInnerHTML={{
                                        __html:
                                          ADMISSION_FORM_TEXT.ADMISSION
                                            .CUSTOM_CONDITION_DETAIL,
                                      }}
                                    />
                                  }
                                  sx={{
                                    ...commonSvgIconStyle,
                                    marginTop: '0.4em',
                                  }}
                                  textFieldProps={{
                                    className: 'w-full',
                                    disabled: isViewMode,
                                    InputLabelProps: { shrink: true },
                                    multiline: true,
                                    name: `conditions.${value}.detail`,
                                    value: values.conditions?.[value].detail,

                                    onChange: handleChange,
                                    onBlur: handleBlur,
                                    error:
                                      touched.conditions?.[value]?.detail &&
                                      Boolean(
                                        errors.conditions?.[value]?.detail,
                                      ),
                                    helperText:
                                      touched.conditions?.[value]?.detail &&
                                      errors.conditions?.[value]?.detail,

                                    rows: 2,
                                  }}
                                />
                              </RadioGroup>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="py-4">{STEP_TEXT.ENTER_CONTACT_INFO}</div>
                    <div className="grid grid-cols-[4fr,4fr,1fr] gap-6 mt-2">
                      <TextField
                        name="contactDepartmentPersonName"
                        required
                        label={CONTACT_PERSON.NAME}
                        disabled={isViewMode}
                        value={values.contactDepartmentPersonName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{ shrink: true }}
                        error={
                          touched.contactDepartmentPersonName &&
                          Boolean(errors.contactDepartmentPersonName)
                        }
                        helperText={
                          touched.contactDepartmentPersonName &&
                          errors.contactDepartmentPersonName
                        }
                      />
                      <PhoneInputField
                        className="w-full"
                        disableDropdown
                        InputLabelProps={{ shrink: true }}
                        disabled={isViewMode}
                        name="contactDepartmentPersonPhone"
                        label={CONTACT_PERSON.PHONE}
                        required
                        value={
                          values.contactDepartmentPersonPhone?.startsWith('852')
                            ? values.contactDepartmentPersonPhone
                            : '852'
                        }
                        onChange={async value => {
                          const phone = value.replace(/[^0-9]/g, '');
                          const phoneNumber = phone.startsWith('852')
                            ? phone.slice(0, 11)
                            : '852';
                          await formik.setFieldValue(
                            'contactDepartmentPersonPhone',
                            phoneNumber,
                          );
                        }}
                        onBlur={handleBlur}
                        error={
                          touched.contactDepartmentPersonPhone &&
                          Boolean(errors.contactDepartmentPersonPhone)
                        }
                        helperText={
                          touched.contactDepartmentPersonPhone &&
                          errors.contactDepartmentPersonPhone
                        }
                      />
                      <div />
                      <TextField
                        disabled={isViewMode}
                        className="w-full"
                        label={CONTACT_PERSON.EMAIL}
                        name="contactDepartmentPersonEmail"
                        value={values.contactDepartmentPersonEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={
                          touched.contactDepartmentPersonEmail &&
                          Boolean(errors.contactDepartmentPersonEmail)
                        }
                        helperText={
                          touched.contactDepartmentPersonEmail &&
                          errors.contactDepartmentPersonEmail
                        }
                      />
                      <DatePicker
                        InputLabelProps={{ shrink: true }}
                        className="w-full"
                        disabled={isViewMode}
                        label={CONTACT_PERSON.CONDITION_DEADLINE}
                        name="conditionFulfillingDeadline"
                        value={
                          checkInvalidDate(values.conditionFulfillingDeadline)
                            ? null
                            : dayjs(values.conditionFulfillingDeadline)
                        }
                        required
                        minDate={dayjs()}
                        maxDate={dayjs(maxMeetConditionDeadline)}
                        onChange={(date: any) => {
                          void setFieldValue(
                            'conditionFulfillingDeadline',
                            date,
                          );
                        }}
                        onBlur={handleBlur}
                        error={
                          touched.conditionFulfillingDeadline &&
                          Boolean(errors.conditionFulfillingDeadline)
                        }
                        helperText={
                          touched.conditionFulfillingDeadline &&
                          errors.conditionFulfillingDeadline
                        }
                      />
                    </div>
                  </>
                )}
              </div>
            </Box>
          </>
        )}
      </div>
    </Box>
  );
};
